import { useEffect, useMemo, useRef } from 'react';
import { i18next } from '@frontend/i18n';
import { useQueueStore } from '@frontend/queue';
import { useAppScopeStore } from '@frontend/scope';
import { useRouter } from '@frontend/weave-router';
import { useAlert } from '@frontend/design-system';

const voicemailHandler = (params: Record<string, string>, allowedLocations: string[]) => {
  const voicemailId = params.voicemailId;
  const locationId = params.locationId;
  const enqueue = useQueueStore.getState().enqueue;

  const locationAllowed = locationId && allowedLocations.includes(locationId);

  if (voicemailId && locationAllowed) {
    const eventId = enqueue('voicemail-details', {
      payload: {
        type: 'query-then-open-detached',
        voicemailId,
        locationId,
      },
      waitForHandler: true,
    });

    return {
      eventId,
      message: '',
    };
  } else if (voicemailId && !locationAllowed) {
    return {
      eventId: null,
      message: i18next.t(
        'Voicemail not allowed for this location. Please switch to the appropriate location to view the voicemail.'
      ),
    };
  } else {
    return {
      eventId: null,
      message: i18next.t('Invalid shortlink.'),
    };
  }
};

const callHistoryHandler = (params: Record<string, string>, allowedLocations: string[]) => {
  const channelId = params.channelId;
  const locationId = params.locationId;
  const enqueue = useQueueStore.getState().enqueue;

  const locationAllowed = locationId && allowedLocations.includes(locationId);

  if (channelId && locationAllowed) {
    const eventId = enqueue('call-insights', {
      payload: {
        channelId,
        locationId,
      },
      waitForHandler: true,
    });
    return {
      eventId,
      message: '',
    };
  } else if (channelId && !locationAllowed) {
    return {
      eventId: null,
      message: i18next.t(
        'Call Record not allowed for this location. Please switch to the appropriate location to view the call record.'
      ),
    };
  } else {
    return {
      eventId: null,
      message: i18next.t('Invalid shortlink.'),
    };
  }
};

const isValidShortlinkUrl = (urlString: string): boolean => {
  try {
    const url = new URL(urlString);

    const allowedHosts = ['getweave.com', 'weavedev.net', 'localhost'];
    const isAllowedHost = allowedHosts.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`));
    if (!isAllowedHost) {
      return false;
    }

    if (url.protocol !== 'https:' && url.hostname !== 'localhost') {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const sanitizeParams = (params: URLSearchParams): Record<string, string> => {
  const sanitized: Record<string, string> = {};
  const allowedParams = ['linktype', 'voicemailId', 'locationId', 'channelId'];

  for (const [key, value] of params.entries()) {
    // Only allow expected parameters
    if (!allowedParams.includes(key)) continue;

    const sanitizedValue = value.replace(/[<>&"']/g, '');
    if (sanitizedValue.length > 100) continue; // Prevent excessively long values

    sanitized[key] = sanitizedValue;
  }

  return sanitized;
};

export const useHandleShortlinks = () => {
  const { selectedLocationIds, forcedGroupId } = useAppScopeStore();
  const topBarRef = useRef<HTMLDivElement>(null);

  const alerts = useAlert();
  const allowedLocations = useMemo(
    () => selectedLocationIds.concat(forcedGroupId ? [forcedGroupId] : []),
    [selectedLocationIds, forcedGroupId]
  );

  useEffect(() => {
    const handler = (e: ClipboardEvent) => {
      const pastedText = e.clipboardData?.getData('text/plain');
      if (!pastedText) return;

      if (!isValidShortlinkUrl(pastedText)) {
        alerts.error(i18next.t('Invalid or untrusted URL'));
        return;
      }

      const url = new URL(pastedText);
      const params = new URLSearchParams(url.search);
      const sanitizedParams = sanitizeParams(params);

      const type = sanitizedParams.linktype;
      if (!type) return;

      let result;
      if (type === 'voicemail') result = voicemailHandler(sanitizedParams, allowedLocations);
      else if (type === 'callHistory') result = callHistoryHandler(sanitizedParams, allowedLocations);
      else result = { eventId: null, message: i18next.t('Invalid shortlink.') };

      if (result?.message) alerts.error(result.message);
    };

    const currentRef = topBarRef.current;
    currentRef?.addEventListener('paste', handler);
    return () => currentRef?.removeEventListener('paste', handler);
  }, [alerts, allowedLocations]);

  return { topBarRef };
};

export const useHandleShortlinksOnAppLoad = () => {
  const router = useRouter();
  const { selectedLocationIds, forcedGroupId } = useAppScopeStore();
  const alerts = useAlert();
  const allowedLocations = useMemo(
    () => selectedLocationIds.concat(forcedGroupId ? [forcedGroupId] : []),
    [selectedLocationIds, forcedGroupId]
  );

  // Maps search string → the allowedLocations key that last produced an error.
  // If locations have changed since the last error, we retry.
  const lastErrorLocationsKey = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    const onLoadHandler = () => {
      const params = new URLSearchParams(window.location.search);
      const sanitizedParams = sanitizeParams(params);

      const type = sanitizedParams.linktype;
      if (!type) return;

      const handledKey = `shortlink:handled:${window.location.search}`;
      if (sessionStorage.getItem(handledKey)) return;

      const locationsKey = allowedLocations.join(',');
      if (lastErrorLocationsKey.current.get(window.location.search) === locationsKey) return;

      let result;
      if (type === 'voicemail') result = voicemailHandler(sanitizedParams, allowedLocations);
      else if (type === 'callHistory') result = callHistoryHandler(sanitizedParams, allowedLocations);
      else result = { eventId: null, message: i18next.t('Invalid shortlink.') };

      if (result?.message) {
        alerts.error(result.message);
        const map = lastErrorLocationsKey.current;
        if (map.size >= 10) map.delete(map.keys().next().value);
        map.set(window.location.search, locationsKey);
      } else if (result?.eventId) {
        sessionStorage.setItem(handledKey, 'true');
        lastErrorLocationsKey.current.delete(window.location.search);
      }
    };

    onLoadHandler();

    const clearHandledKeys = () => {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key?.startsWith('shortlink:handled:')) sessionStorage.removeItem(key);
      }
    };
    window.addEventListener('beforeunload', clearHandledKeys);

    const unsubscribe = router.subscribe('onLoad', () => {
      onLoadHandler();
    });

    return () => {
      window.removeEventListener('beforeunload', clearHandledKeys);
      unsubscribe();
    };
  }, [alerts, allowedLocations, router]);
};
